import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { email, form, minLength, required } from '@angular/forms/signals';
import { ColDef, GridOptions } from 'ag-grid-community';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { defaultTableDefOptions } from '../../../helpers';
import { ApiKeyService } from '../../../service/api-key.service';
import { NotifyService } from '../../../service/notify.service';
import { UserRoles } from '../../../types';
import { ApiKey } from '../../../types/api-keys';
import { CellApikeyActionsComponent } from './cell-apikey-actions/cell-apikey-actions.component';
import { CellApikeyHeaderActionsComponent } from './cell-apikey-header-actions/cell-apikey-header-actions.component';

const defaultForm = () => ({
  created_at: new Date(),
  email: '',
  is_active: true,
  key: '',
  owner_name: '',
  roles: {
    branch: {},
    location: {},
    location_group: {},
    system: [],
  },
});

@Component({
  selector: 'app-apikey-management',
  templateUrl: './apikey-management.component.html',
  styleUrls: ['./apikey-management.component.scss'],
  standalone: false,
})
export class ApikeyManagementComponent implements OnInit {
  private apiKeyService = inject(ApiKeyService);
  private notifyService = inject(NotifyService);

  public isLoading = signal<boolean>(false);
  public tableData = signal<ApiKey[]>([]);

  public apiKeyModal = viewChild<PanModalComponent>('newAPIKeyModal');
  public newApiKey = signal<ApiKey>(defaultForm());

  public newApiKeyForm = form(this.newApiKey, (schema) => {
    required(schema.owner_name);
    minLength(schema.owner_name, 4);

    required(schema.email);
    email(schema.email);
  });

  public isFormValid = computed(() => this.newApiKeyForm().valid());

  public newRoles = signal<UserRoles>({
    branch: {},
    location: {},
    location_group: {},
    system: [],
  });

  public readonly tableDef: GridOptions<ApiKey> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 20,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly columnDefs: ColDef<ApiKey>[] = [
    {
      field: 'key',
      headerName: 'API Key',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      maxWidth: 320,
      width: 320,
    },
    {
      field: 'email',
      headerName: 'Email',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      minWidth: 200,
    },
    {
      field: 'owner_name',
      headerName: 'Owner Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      minWidth: 200,
    },
    {
      field: 'is_active',
      headerName: 'Is Active?',
      width: 120,
      maxWidth: 120,
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      sortable: true,
      sort: 'desc',
      width: 150,
      maxWidth: 150,
    },
    {
      field: 'last_used_at',
      headerName: 'Last Used At',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      sortable: true,
      width: 150,
      maxWidth: 150,
    },
    {
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      width: 140,
      maxWidth: 140,
      headerComponent: CellApikeyHeaderActionsComponent,
      headerClass: ['w-full'],
      headerComponentParams: {
        addCallback: () => {
          this.reset();
          this.apiKeyModal().show();
        },
      },
      sortable: false,
      cellRenderer: CellApikeyActionsComponent,
      cellRendererParams: {
        editCallback: (key: ApiKey) => {
          this.newApiKey.update(() => ({
            ...key,
          }));
          this.newRoles.set(key.roles);
          this.apiKeyModal().show();
        },
        toggleCallback: (key: ApiKey) => {
          const newState = !key.is_active;
          this.apiKeyService.updateApiKeyActiveStatus(key.key, newState).subscribe({
            next: () => {
              this.tableData.update((keys) => {
                const targetKey = keys.find((k) => k.key === key.key);
                if (targetKey) {
                  targetKey.is_active = newState;
                }

                // this updates the object reference so it updates the table correctly
                return [...keys];
              });

              this.notifyService.notify(`API Key has been ${newState ? 'enabled' : 'disabled'}.`);
            },
          });
        },
      },
    },
  ];

  ngOnInit() {
    this.loadAPIKeys();
  }

  loadAPIKeys() {
    this.isLoading.set(true);

    this.apiKeyService.getApiKeys().subscribe({
      next: (keys) => {
        const items = keys.items.map((key) => ({
          ...key,
          created_at: new Date(key.created_at),
          last_used_at: key.last_used_at ? new Date(key.last_used_at) : undefined,
        }));

        this.tableData.set(items);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  saveAPIKey() {
    this.newApiKey.update(() => ({
      ...this.newApiKey(),
      roles: this.newRoles(),
    }));

    if (this.newApiKey().key) {
      this.apiKeyService.updateApiKey(this.newApiKey().key, this.newApiKey()).subscribe({
        next: (updatedKey) => {
          this.tableData.update((keys) => {
            const key = keys.find((k) => k.key === updatedKey.key);
            if (!key) return;

            Object.assign(key, {
              ...updatedKey,
              created_at: new Date(updatedKey.created_at),
              last_used_at: updatedKey.last_used_at ? new Date(updatedKey.last_used_at) : undefined,
            });
            return [...keys];
          });

          this.notifyService.notify('API Key updated successfully.');
        },
      });
      return;
    }

    this.apiKeyService.createApiKey(this.newApiKey()).subscribe({
      next: (key) => {
        this.tableData.update((keys) => [
          {
            ...key,
            created_at: new Date(key.created_at),
            last_used_at: key.last_used_at ? new Date(key.last_used_at) : undefined,
          },
          ...keys,
        ]);

        this.reset();

        this.notifyService.notify('New API Key created successfully.');
      },
    });
  }

  private reset() {
    this.newApiKeyForm().reset(defaultForm());
    this.newRoles.set(defaultForm().roles);
  }
}
